import { useEffect } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AppRoutes } from 'constants/routes-paths.constants';
import { REQUIRED_VALIDATION } from 'constants/validation-rules.constants';
import { useBeforeRouteChange } from 'hooks/useBeforeRouteChange';
import { CountryInput } from 'interfaces/countries.interfaces';
import { asyncCreateCountry, asyncGetCountry, asyncUpdateCountry, unsetCountry } from 'modules/countries/action';
import { selectCountry } from 'modules/countries/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { Card } from 'shared-components/Card';
import { Input } from 'shared-components/Input';
import { ItemNavigation } from 'shared-components/ItemNavigation';

export const CountryForm = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const country = useAppSelector(selectCountry);

  const { formatMessage } = useIntl();

  const {
    reset,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<CountryInput>();

  const onSubmit: SubmitHandler<CountryInput> = (data) => {
    dispatch(id ? asyncUpdateCountry({ id: +id, ...data }) : asyncCreateCountry(data))
      .unwrap()
      .then(() => {
        navigate(AppRoutes.countries);
      })
      .catch(() => 'error');
  };

  useBeforeRouteChange(() => dispatch(unsetCountry()));

  useEffect(() => {
    if (id) {
      dispatch(asyncGetCountry(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      reset({ name: country?.name ?? '' });
    }
  }, [id, reset, country]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card title='general_info' wrapperClassName='w-full md:w-1/2'>
        <div className='flex flex-col gap-4'>
          <Input
            {...register('name', REQUIRED_VALIDATION)}
            error={errors['name']?.message}
            label='title'
            isRequired
            placeholder={formatMessage({ id: 'title' })}
            data-test='country-name'
          />
          <ItemNavigation navigateBack={AppRoutes.countries} />
        </div>
      </Card>
    </form>
  );
};
